import React, { FocusEvent, useEffect, useState } from 'react';

import NumberFormat, {
  NumberFormatValues,
  SourceInfo,
} from 'react-number-format';
import { getPrecision, numberPrettify } from '../../helpers/calc';
import { CustomNumberProps } from './types';
import { Loading } from '../UI';
import { NumberAbbreviationEnum } from '../../constants/enums';

export const CustomNumber = ({
  allowNegative = true,
  assistiveText,
  className = '',
  decimalScale,
  decimalScaleOnFocus,
  defaultValue,
  isDisabled = false,
  displayType,
  id,
  isAllowed = () => true,
  label,
  loading,
  max,
  min,
  name,
  placeholder,
  prefix = '',
  readOnly = true,
  showLabel = false,
  fixedLabel = false,
  suffix = '',
  suffixOverlay = false,
  thousandSeparator = true,
  threshold,
  value = null,
  validate,
  onBlur,
  onFocusCapture,
  onKeyDown,
  onKeyUp,
  onValueChange,
}: CustomNumberProps) => {
  const [isFocusOn, setIsFocusOn] = useState(false);
  const [labelShown, setLabelShown] = useState(fixedLabel);
  const [isTouched, setIsTouched] = useState(false);

  const prettifyThreshold =
    displayType === 'text'
      ? NumberAbbreviationEnum.B
      : NumberAbbreviationEnum.U;

  useEffect(() => {
    isFocusOn && setIsFocusOn(false);
    if (value === null || value === undefined) {
      setLabelShown(false);
      setIsTouched(false);
    } else {
      setLabelShown(true);
    }
  }, [value]);

  if (decimalScale === undefined && typeof value === 'number') {
    decimalScale =
      suffix.includes('%') && value === 100
        ? 0
        : suffix.includes('%')
        ? 2
        : getPrecision(value);
  }

  decimalScaleOnFocus = suffix.includes('%') ? 2 : decimalScaleOnFocus;

  let formattedValue = value;
  if (typeof value === 'number') {
    const { formattedValue: newFormattedValue, suffix: newSuffix } =
      numberPrettify(value, suffix, prettifyThreshold);

    formattedValue = newFormattedValue;
    suffix = newSuffix;
  }

  const handleOnBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    if (!readOnly) {
      setIsFocusOn(false);
      onBlur?.(e);
      if (value === null || value === undefined) {
        setLabelShown(false);
      }
    }
  };

  const handleOnFocusCapture = (e: FocusEvent<HTMLInputElement, Element>) => {
    if (!readOnly) {
      setIsFocusOn(true);
      onFocusCapture?.(e);
      setLabelShown(true);
    }
  };

  const handleValueChange = (
    values: NumberFormatValues,
    sourceInfo: SourceInfo
  ) => {
    setIsTouched(true);
    onValueChange?.(values, sourceInfo);
  };

  const isValid = validate?.(value);

  return (
    <>
      {showLabel ? (
        <div>
          <div
            className={`relative border-b-1 pb-2 ${
              isDisabled
                ? 'border-grey-30'
                : isTouched && validate
                ? isValid
                  ? 'border-positive'
                  : 'border-negative'
                : 'border-black'
            } ${className}`}
          >
            {labelShown && (
              <label
                htmlFor={id}
                className={`absolute -top-4 left-0 text-xs font-medium ${
                  isDisabled
                    ? 'text-grey-30 dark:text-grey-70'
                    : 'text-black dark:text-white'
                } `}
              >
                {label}
              </label>
            )}
            <NumberFormat
              allowNegative={allowNegative}
              className={`mt-1.5 ${
                isDisabled ? 'text-grey-30' : 'text-black'
              } bg-transparent placeholder-c`}
              decimalScale={isFocusOn ? decimalScaleOnFocus : decimalScale}
              defaultValue={defaultValue}
              disabled={isDisabled}
              displayType={displayType}
              fixedDecimalScale={true}
              id={id}
              isAllowed={isAllowed}
              max={max}
              min={min}
              name={name}
              readOnly={readOnly}
              placeholder={showLabel && labelShown ? placeholder : label}
              prefix={isFocusOn ? undefined : prefix}
              suffix={suffixOverlay || isFocusOn ? undefined : suffix}
              thousandSeparator={thousandSeparator}
              value={
                isFocusOn
                  ? value
                  : formattedValue !== null
                  ? formattedValue
                  : ''
              }
              onBlur={handleOnBlur}
              onFocusCapture={handleOnFocusCapture}
              onKeyDown={onKeyDown}
              onKeyUp={onKeyUp}
              onValueChange={handleValueChange}
            />
            {suffixOverlay && suffix && (
              <span
                className={`absolute right-0 bottom-0 pb-2 ${
                  isDisabled ? 'text-grey-30' : 'text-grey-50'
                }`}
              >
                {suffix}
              </span>
            )}
            <Loading
              size={'small'}
              status={loading}
              className="bg-white dark:bg-black absolute bottom-2.5 -right-0.5 text-black text-lg pointer-events-none dark:border-grey-70 dark:text-white"
            />
          </div>
          <small className="pt-2 block text-xs w-full text-left text-grey-50">
            {assistiveText}
          </small>
        </div>
      ) : (
        <NumberFormat
          allowNegative={allowNegative}
          className={`${className} bg-transparent`}
          decimalScale={isFocusOn ? decimalScaleOnFocus : decimalScale}
          defaultValue={defaultValue}
          disabled={isDisabled}
          displayType={displayType}
          fixedDecimalScale={true}
          id={id}
          isAllowed={isAllowed}
          max={max}
          min={min}
          name={name}
          readOnly={readOnly}
          placeholder={placeholder}
          prefix={isFocusOn ? undefined : prefix}
          suffix={isFocusOn ? undefined : suffix}
          thousandSeparator={thousandSeparator}
          value={isFocusOn ? value : formattedValue}
          onBlur={handleOnBlur}
          onFocusCapture={handleOnFocusCapture}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onValueChange={onValueChange}
        />
      )}
    </>
  );
};
